import { SlotKey, StartStage, FormState } from './formTypes';

// Match-existing catalog mode is switched off for now. Flip to true to restore the
// mode card and the observation/solutions catalog slots it feeds; the branches for
// both are still in place behind this flag (see formRules.slotStates).
export const MATCH_EXISTING_ENABLED = false;

// Resume mode is switched off for now — the backend stages it drives aren't wired up
// yet. Flip to true to enable the mode card and the previous-run/start-stage selects
// it reveals; the branches stay behind this flag so they keep their test coverage.
export const RESUME_ENABLED = false;

export const SLOT_KEYS: SlotKey[] = [
    'historical_cases',
    'part_info',
    'error_codes',
    'solution_codes_filename',
    'observation_catalog',
    'solutions_catalog',
    'diagnostic_component',
];

// Slots rendered inside the Advanced accordion, each under the clustering toggle
// that consumes it — not in the main Files section.
export const CLUSTERING_SLOT_KEYS: SlotKey[] = [
    'error_codes',
    'solution_codes_filename',
    'diagnostic_component',
];

export const SLOT_LABELS: Record<SlotKey, string> = {
    historical_cases: 'Historical cases',
    part_info: 'Part replacement history',
    error_codes: 'Error codes',
    solution_codes_filename: 'Solution codes',
    observation_catalog: 'Observation catalog',
    solutions_catalog: 'Solutions catalog',
    diagnostic_component: 'Component codes (diagnostics)',
};

export type ColumnHelp = { name: string; description: string };

// Expected columns per file slot, shown by each FileSlot's help button.
// Hovering lists just the names; clicking adds each description.
// 'observation_catalog' and 'solutions_catalog' are intentionally left
// empty — they belong to the match-existing-catalog mode, which is
// currently disabled (see MATCH_EXISTING_ENABLED above).
export const SLOT_COLUMN_HELP: Record<SlotKey, ColumnHelp[]> = {
    historical_cases: [
        { name: 'investigation_id', description: 'Unique identifier for the investigation/visit.' },
        { name: 'visit_date', description: 'Date the visit/investigation took place.' },
        {
            name: 'Note',
            description:
                'Also needs one or more free-text columns (e.g. complaint, cause, correction) — these are customer-specific and defined per dataset, not a fixed header named "free-text columns". Part information (part_id, part_description) is supplied separately via the Part replacement history file.',
        },
    ],
    part_info: [
        { name: 'part_id', description: 'Identifier of the replaced part.' },
        { name: 'part_description', description: 'Description of the replaced part.' },
        { name: 'Note', description: 'Can be generated from investigation data.' },
    ],
    error_codes: [
        { name: 'final_value', description: 'The canonical error code to display.' },
        { name: 'matching_value', description: 'Every reasonable permutation of how the code might appear in free text — one row per permutation.' },
        { name: 'Note', description: 'Required for error-code clustering.' },
    ],
    solution_codes_filename: [
        { name: 'final_value', description: 'The canonical solution code to display.' },
        { name: 'matching_value', description: 'Every reasonable permutation of how the code might appear in free text — one row per permutation.' },
        { name: 'Note', description: 'Required for solution-code clustering.' },
    ],
    observation_catalog: [],
    solutions_catalog: [],
    diagnostic_component: [
        { name: 'Component_name', description: 'Name of the diagnostic component.' },
        { name: 'code', description: 'Component code.' },
        { name: 'Note', description: 'Similar to part ID, but can cluster by multiple parts per label.' },
    ],
};

export type AdvancedOptionKey = Extract<
    keyof FormState,
    | 'productType'
    | 'maxCasesEnabled'
    | 'useCustomInstructions'
    | 'useSolutionCodeNonSemanticClustering'
    | 'ruleBasedObsLabelMerge'
    | 'useExactMatchErrorCodeClustering'
    | 'useExactMatchSolutionCodeClustering'
    | 'clusterDiagnosticsByComponents'
    | 'useCustomPostProcessing'
    | 'partIdAsSolutionName'
>;

// Plain-text explanation per Advanced-section option, shown by that option's
// help button (same text whether hovering or pinned). 'productType' and
// 'maxCasesEnabled' are intentionally left empty pending copy. 'maxCasesEnabled'
// covers both the "Limit max cases" switch and the adjoining "Max cases" field —
// one logical setting.
export const ADVANCED_OPTION_HELP: Record<AdvancedOptionKey, string> = {
    productType: '',
    maxCasesEnabled: '',
    useCustomInstructions:
        'If you do not have any custom instructions, this will have no impact on results.',
    useSolutionCodeNonSemanticClustering:
        'Does the freetext contain solution codes which can be picked up according to a defined pattern using regular expressions?\nIf so, turn on and define pattern. Otherwise, turn off.',
    ruleBasedObsLabelMerge:
        'Rule based semantic merge of similar labels. Leave this on unless observation clustering for previous results has not been sensitive enough.',
    useExactMatchErrorCodeClustering:
        'If you have known error codes, uploading them here will greatly improve observation clustering. The file needs a final_value column (the canonical code to display) and a matching_value column listing every reasonable permutation of how that code might appear in free text — one row per permutation. Example for VCU 000100.34:\n\n' +
        'final_value | matching_value\n' +
        'VCU 000100.34 | VCU 000100.34\n' +
        'VCU 000100.34 | VCU000100.34\n' +
        'VCU 000100.34 | 000100.34\n' +
        'VCU 000100.34 | 00100.34\n' +
        'VCU 000100.34 | 0100.34\n' +
        'VCU 000100.34 | 100.34',
    useExactMatchSolutionCodeClustering:
        'Same concept as clustering by exact matches for error codes.',
    clusterDiagnosticsByComponents:
        'Cluster diagnostic actions based on the components they involve. Similar to part-based clustering. If diagnostics is on, and diagnostic actions frequently refer to specific components, and there is a list of those components in a csv, this can help with diagnostics clustering.',
    useCustomPostProcessing:
        "Use this if there's additional rule-based filtering you want applied that the pipeline doesn't already take care of automatically. Define the rules below.",
    partIdAsSolutionName:
        'Controls how a solution label that references a part ID is handled. Off: leave the label as-is, e.g. "Replaced bolt XYZ to tighten chassis". Replace: swap the label for the part\'s information/description, e.g. "Bolt XYZ". Duplicate: keep the original label and also add a second label with just the part info, so "Replaced bolt XYZ to tighten chassis" produces both that label and "Bolt XYZ" — but if the label were only "Replaced bolt XYZ" with no extra detail, just the part-info label is used.',
};

export const START_STAGES: StartStage[] = [
    'simplification',
    'clustering',
    'catalog_merge',
    'post_processing',
];

export const DEFAULT_MAX_CASES = 10000;

export const DEFAULT_NON_SEMANTIC_CLUSTERING_RULES = JSON.stringify(
    [
        {
            type: 'contains',
            pattern: '(?<![A-Za-z0-9])(\\d{5}|\\d{6})(?!\\d)(?!\\.\\d)',
            contains: 'dtac',
            flags: 0,
            label_suffix: '',
            label_prefix: 'DTAC Solution #',
        },
    ],
    null,
    2,
);

export const DEFAULT_CUSTOM_POST_PROCESSING_RULES = JSON.stringify(
    [
        {
            label_type: 'solutions',
            contains: 'DTAC',
            pattern: '\\d{6}\\.\\d{2}',
            action: 'remove',
        },
    ],
    null,
    2,
);

export const initialForm: FormState = {
    tenantId: null,
    runMode: null,
    previousRun: null,
    startStage: null,
    extractObservations: true,
    extractSolutions: true,
    extractDiagnostics: true,
    catalogMode: 'generate_fresh',
    productType: '',
    useCustomInstructions: false,
    useSolutionCodeNonSemanticClustering: false,
    ruleBasedObsLabelMerge: true,
    useExactMatchErrorCodeClustering: false,
    useExactMatchSolutionCodeClustering: false,
    clusterDiagnosticsByComponents: false,
    solutionCodeNonSemanticClusteringRules: DEFAULT_NON_SEMANTIC_CLUSTERING_RULES,
    useCustomPostProcessing: false,
    customPostProcessingRules: DEFAULT_CUSTOM_POST_PROCESSING_RULES,
    partIdAsSolutionName: 'replace',
    maxCasesEnabled: true,
    maxCases: DEFAULT_MAX_CASES,
    columnDefinitions: [{ columns: [], definition: '', relevantTo: [] }],
    files: {
        historical_cases: null,
        error_codes: null,
        solution_codes_filename: null,
        observation_catalog: null,
        solutions_catalog: null,
        part_info: null,
        diagnostic_component: null,
    },
    fileColumns: {},
};
